import React from 'react'
import { Icon, Element } from 'react-bulma-components'
import { InfoIcon } from '../../../icons'
import { createColumns } from '../utils'
import { ColumnDefinition, ColumnGroupDefinition, TableRowData } from '../types'
import { WeekSummary } from '../../../../../../backend/src/services/resources/missions/missions.summary.service.types'
import { DurationCell, EmployerRuleCell } from '../table-cells'
import { isNumber } from 'lodash'
import { getDocIdAsString } from '../../../../utils/documents'

interface TotalsDurationsColumnGroupProps {
  hideActions?: boolean
  minimalMode?: boolean
  employerRules: WeekSummary['employerRules']
}

const buildEmployerRulesColumns = (
  rule: WeekSummary['employerRules'][0],
  minimalMode: boolean,
  index: number,
  totalsEmployerRulesCount?: number,
) => ({
  header: rule.shortName,
  id: `durations.${rule.name}`,
  shouldMerge: true,
  isHidden: minimalMode,
  render: (data: TableRowData) => {
    const value =
      data.totals.employerData.durations[getDocIdAsString(rule.ruleId)] ||
      data.totals.employerData.goals[getDocIdAsString(rule.ruleId)] ||
      data.totals.employerData.costs[getDocIdAsString(rule.ruleId)]
    return (
      <EmployerRuleCell
        employerRule={rule}
        value={value ?? null}
        fallbackTexts={{
          goal: '0',
          multiply: '00h00',
          add: '00h00',
        }}
      />
    )
  },
  className:
    isNumber(totalsEmployerRulesCount) && index === totalsEmployerRulesCount - 1
      ? 'table-section-end'
      : '',
})

const TotalsDurationsColumnsGroup = ({
  hideActions,
  minimalMode,
  employerRules,
}: TotalsDurationsColumnGroupProps): ColumnGroupDefinition => {
  const totalsEmployerRules = employerRules.filter(
    rule => rule.addToWorkedTotal && rule.display.summary.totals,
  )
  const otherEmployerRules = employerRules.filter(
    rule => !rule.addToWorkedTotal && rule.display.summary.totals,
  )
  const employerRulesColumns: ColumnDefinition[] = otherEmployerRules.map((rule, index) =>
    buildEmployerRulesColumns(rule, minimalMode ?? false, index, otherEmployerRules.length),
  )
  const totalsEmployerRulesColumns = totalsEmployerRules.map((rule, index) =>
    buildEmployerRulesColumns(rule, minimalMode ?? false, index),
  )
  return {
    Header: () => (
      <Element display="flex">
        Totaux{' '}
        {!hideActions && (
          <Icon
            size="small"
            color="primary"
            data-tooltip="Ce total est basé sur les filtres utilisés. Il peut ne pas être représentatif du total complet de la semaine."
            className="has-tooltip-multiline has-tooltip-bottom"
            ml={1}
          >
            <InfoIcon />
          </Icon>
        )}
      </Element>
    ),
    id: 'Totaux',
    columns: createColumns([
      {
        header: 'Jour',
        id: 'totals.durations.worked.day',
        isHidden: minimalMode,
        render: (data: TableRowData) => (
          <DurationCell duration={data.durations.worked.day} fallbackText="00h00" />
        ),
        shouldMerge: true,
        className: !minimalMode ? 'table-section-start' : '',
      },
      {
        header: 'Nuit',
        id: 'totals.durations.worked.night',
        isHidden: minimalMode,
        render: (data: TableRowData) => (
          <DurationCell duration={data.durations.worked.night} fallbackText="00h00" />
        ),
        shouldMerge: true,
      },
      {
        header: 'Travaillé',
        id: 'totals.durations.worked.total',
        isHidden: minimalMode,
        render: (data: TableRowData) => (
          <DurationCell duration={data.durations.worked.total} fallbackText="00h00" />
        ),
        shouldMerge: true,
      },
      {
        header: (
          <Element data-tooltip="Durée total du travail non effectif, journées avec évènements prestations">
            N.Effectif
          </Element>
        ),
        id: 'totals.durations.benefit',
        render: (data: TableRowData) => (
          <DurationCell duration={data.durations.benefit} fallbackText="00h00" />
        ),
        isHidden: minimalMode,
        shouldMerge: true,
      },
      ...totalsEmployerRulesColumns,
      {
        header: 'Total',
        id: 'totals.durations.total.total',
        render: (data: TableRowData) => (
          <DurationCell duration={data.durations.total.total} fallbackText="00h00" />
        ),
        shouldMerge: true,
        className: minimalMode
          ? 'table-section-start table-section-end'
          : !employerRulesColumns[0]
            ? 'table-section-end'
            : '',
      },
      //{
      //  header: 'Supp',
      //  id: 'totals.durations.overtime.total',
      //  isHidden: minimalMode,
      //  render: (data: TableRowData) => (
      //    <DurationCell duration={data.durations.overtime.total} fallbackText="00h00" />
      //  ),
      //  shouldMerge: true,
      //  className: !employerRulesColumns[0] ? 'table-section-end' : '',
      //},
      ...employerRulesColumns,
    ]),
  }
}

export default TotalsDurationsColumnsGroup
